import {agendaDirectory} from "../access/patientDirectory";
import {
    getDeviceTimeZone,
    localizeScheduleEntry
} from "../utils/timezone";
import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    X,
    CalendarDays,
    MessageCircle,
    CircleDollarSign,
    Bell,
    CheckCheck
} from "lucide-react";

import {
    collection,
    onSnapshot,
    writeBatch,
    doc
} from "../access/firestore";

import {
    db
} from "../firebase";

import {
    useClinic
} from "../context/ClinicContext";


export default function MobileNotificationsPanel({
    open,
    onClose,
    onNavigate,
    onUnreadCountChange,
    permissions = {}
}) {

    const {
        clinicId
    } = useClinic();
    const deviceTimeZone = getDeviceTimeZone();
    const canReadAppointments = Boolean(permissions.citas);
    const canReadPayments = Boolean(permissions.finanzas);
    const canReadWhatsApp = Boolean(permissions.whatsapp);


    const [
        citas,
        setCitas
    ] = useState([]);


    const [
        pagos,
        setPagos
    ] = useState([]);


    const [
        conversaciones,
        setConversaciones
    ] = useState([]);


    // =========================================================
    // NOTIFICACIONES YA LEÍDAS EN ESTE DISPOSITIVO
    // =========================================================

    const [
        readNotificationIds,
        setReadNotificationIds
    ] = useState([]);


    useEffect(() => {

        if (!clinicId) {
            setReadNotificationIds([]);
            return;
        }

        try {

            const storageKey =
                `vexadent_read_notifications_${clinicId}`;

            const stored =
                localStorage.getItem(
                    storageKey
                );

            if (stored) {

                const parsed =
                    JSON.parse(stored);

                setReadNotificationIds(
                    Array.isArray(parsed)
                        ? parsed
                        : []
                );

            } else {

                setReadNotificationIds([]);

            }

        } catch (error) {

            console.error(
                "Error cargando notificaciones leídas:",
                error
            );

            setReadNotificationIds([]);

        }

    }, [clinicId]);

    // =========================================================
    // CONVERTIR FECHAS DE FIREBASE A MILISEGUNDOS
    // =========================================================

    const toMillis = (value) => {

        if (!value) {
            return 0;
        }

        if (
            typeof value.toMillis ===
            "function"
        ) {
            return value.toMillis();
        }

        if (
            value instanceof Date
        ) {
            return value.getTime();
        }

        if (
            typeof value ===
            "number"
        ) {
            return value;
        }

        const parsed =
            new Date(value).getTime();

        return Number.isNaN(parsed)
            ? 0
            : parsed;

    };


    // =========================================================
    // ESCUCHAR CITAS
    // =========================================================

    useEffect(() => {

        setCitas([]);
        if (!clinicId || !canReadAppointments) {

            setCitas([]);

            return;

        }


        const ref =
            agendaDirectory(db,clinicId);


        const unsubscribe =
            onSnapshot(
                ref,
                snapshot => {

                    const data =
                        snapshot.docs.map(
                            item => localizeScheduleEntry({

                                id:
                                    item.id,

                                ...item.data()

                            }, deviceTimeZone)
                        );


                    setCitas(
                        data
                    );

                },
                error => {

                    console.error(
                        "Error cargando citas para notificaciones:",
                        error
                    );

                }
            );


        return () =>
            unsubscribe();

    }, [clinicId, canReadAppointments, deviceTimeZone]);


    // =========================================================
    // ESCUCHAR PAGOS
    // =========================================================

    useEffect(() => {

        setPagos([]);
        if (!clinicId || !canReadPayments) {

            setPagos([]);

            return;

        }


        const ref =
            collection(
                db,
                "clinics",
                clinicId,
                "pagos"
            );


        const unsubscribe =
            onSnapshot(
                ref,
                snapshot => {

                    const data =
                        snapshot.docs.map(
                            item => ({

                                id:
                                    item.id,

                                ...item.data()

                            })
                        );


                    setPagos(
                        data
                    );

                },
                error => {

                    console.error(
                        "Error cargando pagos para notificaciones:",
                        error
                    );

                }
            );


        return () =>
            unsubscribe();

    }, [clinicId, canReadPayments]);


    // =========================================================
    // ESCUCHAR WHATSAPP
    // =========================================================

    useEffect(() => {

        setConversaciones([]);
        if (!clinicId || !canReadWhatsApp) {

            setConversaciones([]);

            return;

        }


        const ref =
            collection(
                db,
                "clinics",
                clinicId,
                "whatsappConversations"
            );


        const unsubscribe =
            onSnapshot(
                ref,
                snapshot => {

                    const data =
                        snapshot.docs.map(
                            item => ({

                                id:
                                    item.id,

                                ...item.data()

                            })
                        );


                    setConversaciones(
                        data
                    );

                },
                error => {

                    console.error(
                        "Error cargando WhatsApp para notificaciones:",
                        error
                    );

                }
            );


        return () =>
            unsubscribe();

    }, [clinicId, canReadWhatsApp]);


    // =========================================================
    // FORMATEAR FECHA
    // =========================================================

    const formatDateTime =
        (value) => {

            const millis =
                toMillis(value);


            if (!millis) {
                return "";
            }


            const date =
                new Date(
                    millis
                );


            return date.toLocaleString(
                "es-MX",
                {
                    day:
                        "2-digit",

                    month:
                        "short",

                    hour:
                        "numeric",

                    minute:
                        "2-digit"
                }
            );

        };


    // =========================================================
    // TIEMPO RELATIVO
    // =========================================================

    const formatRelativeTime =
        (value) => {

            const millis =
                toMillis(value);


            if (!millis) {
                return "";
            }


            const difference =
                Date.now() -
                millis;


            if (
                difference <
                60 * 1000
            ) {

                return "Hace unos segundos";

            }


            if (
                difference <
                60 * 60 * 1000
            ) {

                const minutes =
                    Math.floor(
                        difference /
                        (60 * 1000)
                    );


                return `Hace ${minutes} min`;

            }


            if (
                difference <
                24 * 60 * 60 * 1000
            ) {

                const hours =
                    Math.floor(
                        difference /
                        (60 * 60 * 1000)
                    );


                return `Hace ${hours} h`;

            }


            const days =
                Math.floor(
                    difference /
                    (24 * 60 * 60 * 1000)
                );


            if (
                days ===
                1
            ) {

                return "Ayer";

            }


            return `Hace ${days} días`;

        };


    // =========================================================
    // FECHA DE CITA
    // =========================================================

    const parseAppointmentDate =
        (cita) => {

            if (
                !cita ||
                !cita.fecha
            ) {

                return 0;

            }


            let dateString =
                "";


            if (
                typeof cita.fecha ===
                "string"
            ) {

                dateString =
                    cita.fecha;

            } else if (
                typeof cita.fecha.toDate ===
                "function"
            ) {

                return cita.fecha
                    .toDate()
                    .getTime();

            } else {

                return toMillis(
                    cita.fecha
                );

            }


            if (
                cita.hora &&
                typeof cita.hora ===
                "string"
            ) {

                const combined =
                    `${dateString}T${cita.hora}`;


                const parsed =
                    new Date(
                        combined
                    ).getTime();


                if (
                    !Number.isNaN(
                        parsed
                    )
                ) {

                    return parsed;

                }

            }


            const parsed =
                new Date(
                    dateString
                ).getTime();


            return Number.isNaN(
                parsed
            )

                ? 0

                : parsed;

        };


    // =========================================================
    // CONSTRUIR NOTIFICACIONES
    // =========================================================

    const notifications =
        useMemo(() => {

            const now =
                Date.now();


            const result =
                [];


            // =================================================
            // CITAS
            // =================================================

            const upcomingAppointments =
                citas
                    .map(
                        cita => ({

                            cita,

                            timestamp:
                                parseAppointmentDate(
                                    cita
                                )

                        })
                    )
                    .filter(
                        item => {

                            if (
                                !item.timestamp
                            ) {

                                return false;

                            }


                            const difference =
                                item.timestamp -
                                now;


                            // Mostrar citas desde
                            // hace 2 horas hasta
                            // 7 días adelante.

                            return (
                                difference >=
                                -2 * 60 * 60 * 1000

                                &&

                                difference <=
                                7 * 24 * 60 * 60 * 1000
                            );

                        }
                    )
                    .sort(
                        (a, b) =>
                            a.timestamp -
                            b.timestamp
                    )
                    .slice(
                        0,
                        5
                    );


            upcomingAppointments
                .forEach(
                    ({
                        cita,
                        timestamp
                    }) => {

                        const isToday =
                            new Date(
                                timestamp
                            ).toDateString() ===
                            new Date()
                                .toDateString();


                        const time =
                            new Date(
                                timestamp
                            ).toLocaleTimeString(
                                "es-MX",
                                {
                                    hour:
                                        "numeric",

                                    minute:
                                        "2-digit"
                                }
                            );


                        result.push({

                            id:
                                `cita-${cita.id}`,

                            type:
                                "appointment",

                            title:
                                "Próxima cita",

                            description:
                                isToday

                                    ? `${cita.pacienteNombre || "Paciente"} tiene una cita hoy a las ${time}.`

                                    : `${cita.pacienteNombre || "Paciente"} tiene una cita el ${formatDateTime(timestamp)}.`,

                            time:
                                cita.createdAt

                                    ? formatRelativeTime(
                                        cita.createdAt
                                    )

                                    : formatDateTime(
                                        timestamp
                                    ),

                            timestamp,

                            unread:
                                false

                        });

                    }
                );


            // =================================================
            // WHATSAPP
            // =================================================

            conversaciones
                .filter(
                    conversation =>
                        Number(
                            conversation.unreadCount
                        ) > 0
                )
                .sort(
                    (a, b) =>
                        toMillis(
                            b.lastMessageAt
                        ) -
                        toMillis(
                            a.lastMessageAt
                        )
                )
                .slice(
                    0,
                    5
                )
                .forEach(
                    conversation => {

                        const count =
                            Number(
                                conversation.unreadCount
                            );


                        result.push({

                            id:
                                `whatsapp-${conversation.id}`,

                            type:
                                "whatsapp",

                            title:
                                "Nuevo mensaje",

                            description:
                                `${conversation.patientName || "Paciente"} tiene ${count} mensaje${count === 1 ? "" : "s"} sin leer.`,

                            time:
                                formatRelativeTime(
                                    conversation.lastMessageAt
                                ),

                            timestamp:
                                toMillis(
                                    conversation.lastMessageAt
                                ),

                            unread:
                                true

                        });

                    }
                );


            // =================================================
            // PAGOS
            // =================================================

            const recentPayments =
                pagos
                    .map(
                        pago => ({

                            pago,

                            timestamp:
                                toMillis(
                                    pago.fecha
                                )

                        })
                    )
                    .filter(
                        item => {

                            if (
                                !item.timestamp
                            ) {

                                return false;

                            }


                            // Últimos 7 días.

                            return (
                                now -
                                item.timestamp
                            ) <=
                            7 * 24 * 60 * 60 * 1000;

                        }
                    )
                    .sort(
                        (a, b) =>
                            b.timestamp -
                            a.timestamp
                    )
                    .slice(
                        0,
                        5
                    );


            recentPayments
                .forEach(
                    ({
                        pago,
                        timestamp
                    }) => {

                        const amount =
                            Number(
                                pago.monto
                            );


                        const formattedAmount =
                            Number.isFinite(
                                amount
                            )

                                ? amount.toLocaleString(
                                    "es-MX",
                                    {
                                        style:
                                            "currency",

                                        currency:
                                            "MXN"
                                    }
                                )

                                : "$0.00 MXN";


                        result.push({

                            id:
                                `pago-${pago.id}`,

                            type:
                                "payment",

                            title:
                                "Pago registrado",

                            description:
                                `${pago.pacienteNombre || "Paciente"} realizó un pago de ${formattedAmount}.`,

                            time:
                                formatRelativeTime(
                                    timestamp
                                ),

                            timestamp,

                            unread:
                                false

                        });

                    }
                );


            // =================================================
            // ORDEN FINAL
            // =================================================

            return result
                .sort(
                    (a, b) =>
                        b.timestamp -
                        a.timestamp
                )
                .slice(
                    0,
                    12
                );

        }, [
            citas,
            pagos,
            conversaciones
        ]);


    // =========================================================
    // FILTRAR NOTIFICACIONES YA LEÍDAS
    // =========================================================

    const visibleNotifications =
        useMemo(() => {

            return notifications.filter(
                notification =>
                    notification.type ===
                        "whatsapp"

                        ||

                    !readNotificationIds.includes(
                        notification.id
                    )
            );

        }, [
            notifications,
            readNotificationIds
        ]);

        useEffect(() => {

            if (onUnreadCountChange) {

                onUnreadCountChange(
                    visibleNotifications.length
                );

            }

        }, [
            visibleNotifications.length,
            onUnreadCountChange
        ]);


    // =========================================================
    // MARCAR TODAS COMO LEÍDAS
    // =========================================================

    const markAllAsRead =
        async () => {

            if (!clinicId) {
                return;
            }


            const unreadConversations =
                conversaciones.filter(
                    conversation =>
                        Number(
                            conversation.unreadCount
                        ) > 0
                );


            try {

                // ---------------------------------------------
                // GUARDAR COMO LEÍDAS LAS NOTIFICACIONES
                // ACTUALES DE CITAS Y PAGOS EN ESTE DISPOSITIVO
                // ---------------------------------------------

                const currentNotificationIds =
                    notifications.map(
                        notification =>
                            notification.id
                    );


                const nextReadIds =
                    [
                        ...new Set([
                            ...readNotificationIds,
                            ...currentNotificationIds
                        ])
                    ];


                const storageKey =
                    `vexadent_read_notifications_${clinicId}`;


                localStorage.setItem(
                    storageKey,
                    JSON.stringify(
                        nextReadIds
                    )
                );


                setReadNotificationIds(
                    nextReadIds
                );


                // ---------------------------------------------
                // MARCAR WHATSAPP COMO LEÍDO EN FIRESTORE
                // ---------------------------------------------

                if (
                    unreadConversations.length ===
                    0
                ) {

                    return;

                }


                const batch =
                    writeBatch(
                        db
                    );


                unreadConversations.forEach(
                    conversation => {

                        const ref =
                            doc(
                                db,
                                "clinics",
                                clinicId,
                                "whatsappConversations",
                                conversation.id
                            );


                        batch.update(
                            ref,
                            {
                                unreadCount:
                                    0
                            }
                        );

                    }
                );


                await batch.commit();

            } catch (error) {

                console.error(
                    "Error marcando notificaciones como leídas:",
                    error
                );

            }

        };


    // =========================================================
    // ICONO
    // =========================================================

    const getIcon =
        (type) => {

            if (
                type ===
                "appointment"
            ) {

                return CalendarDays;

            }


            if (
                type ===
                "whatsapp"
            ) {

                return MessageCircle;

            }


            if (
                type ===
                "payment"
            ) {

                return CircleDollarSign;

            }


            return Bell;

        };


    // =========================================================
    // PANEL CERRADO
    // =========================================================

    if (!open) {

        return null;

    }


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <>

            {/* FONDO */}

            <div
                onClick={
                    onClose
                }

                style={{
                    position:
                        "fixed",

                    inset:
                        0,

                    background:
                        "rgba(15,23,42,0.32)",

                    backdropFilter:
                        "blur(2px)",

                    WebkitBackdropFilter:
                        "blur(2px)",

                    zIndex:
                        1998
                }}
            />


            {/* PANEL */}

            <div
                style={{
                    position:
                        "fixed",

                    top:
                        0,

                    right:
                        0,

                    bottom:
                        0,

                    width:
                        "min(92vw, 390px)",

                    background:
                        "#f8fafc",

                    zIndex:
                        1999,

                    display:
                        "flex",

                    flexDirection:
                        "column",

                    boxShadow:
                        "-10px 0 30px rgba(15,23,42,0.14)",

                    paddingTop:
                        "env(safe-area-inset-top)"
                }}
            >

                {/* HEADER */}

                <div
                    style={{
                        minHeight:
                            70,

                        padding:
                            "12px 16px",

                        background:
                            "#ffffff",

                        borderBottom:
                            "1px solid #e8edf5",

                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "space-between",

                        boxSizing:
                            "border-box"
                    }}
                >

                    <div>

                        <div
                            style={{
                                fontSize:
                                    18,

                                fontWeight:
                                    750,

                                color:
                                    "#0f172a"
                            }}
                        >
                            Notificaciones
                        </div>


                        <div
                            style={{
                                marginTop:
                                    3,

                                fontSize:
                                    12,

                                color:
                                    "#64748b"
                            }}
                        >
                            Actividad reciente
                        </div>

                    </div>


                    <button
                        type="button"

                        onClick={
                            onClose
                        }

                        aria-label="Cerrar"

                        style={{
                            width:
                                40,

                            height:
                                40,

                            borderRadius:
                                12,

                            border:
                                "1px solid #e5eaf2",

                            background:
                                "#ffffff",

                            color:
                                "#475569",

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            cursor:
                                "pointer",

                            WebkitTapHighlightColor:
                                "transparent"
                        }}
                    >

                        <X
                            size={
                                20
                            }

                            strokeWidth={
                                1.9
                            }
                        />

                    </button>

                </div>


                {/* ACCIONES */}

                <div
                    style={{
                        padding:
                            "12px 16px 8px",

                        display:
                            "flex",

                        justifyContent:
                            "flex-end"
                    }}
                >

                    <button
                        type="button"

                        onClick={
                            markAllAsRead
                        }

                        disabled={
                            visibleNotifications.length ===
                            0
                        }

                        style={{
                            border:
                                "none",

                            background:
                                "transparent",

                            color:
                                visibleNotifications.length ===
                                0

                                    ? "#94a3b8"

                                    : "#2563eb",

                            fontSize:
                                12,

                            fontWeight:
                                650,

                            display:
                                "flex",

                            alignItems:
                                "center",

                            gap:
                                6,

                            padding:
                                "6px 0",

                            cursor:
                                visibleNotifications.length ===
                                0

                                    ? "default"

                                    : "pointer",

                            opacity:
                                visibleNotifications.length ===
                                0

                                    ? 0.7

                                    : 1
                        }}
                    >

                        <CheckCheck
                            size={
                                16
                            }

                            strokeWidth={
                                1.9
                            }
                        />

                        Marcar todas como leídas

                    </button>

                </div>


                {/* LISTA */}

                <div
                    style={{
                        flex:
                            1,

                        overflowY:
                            "auto",

                        padding:
                            "4px 12px 24px",

                        WebkitOverflowScrolling:
                            "touch"
                    }}
                >

                    {visibleNotifications.length ===
                    0 ? (

                        <div
                            style={{
                                minHeight:
                                    260,

                                display:
                                    "flex",

                                flexDirection:
                                    "column",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",

                                textAlign:
                                    "center",

                                padding:
                                    "20px"
                            }}
                        >

                            <div
                                style={{
                                    width:
                                        52,

                                    height:
                                        52,

                                    borderRadius:
                                        16,

                                    background:
                                        "#eff6ff",

                                    color:
                                        "#2563eb",

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center",

                                    marginBottom:
                                        12
                                }}
                            >

                                <Bell
                                    size={
                                        24
                                    }

                                    strokeWidth={
                                        1.8
                                    }
                                />

                            </div>


                            <div
                                style={{
                                    fontSize:
                                        15,

                                    fontWeight:
                                        700,

                                    color:
                                        "#0f172a"
                                }}
                            >
                                Todo al día
                            </div>


                            <div
                                style={{
                                    marginTop:
                                        5,

                                    fontSize:
                                        12.5,

                                    lineHeight:
                                        1.45,

                                    color:
                                        "#64748b",

                                    maxWidth:
                                        260
                                }}
                            >
                                No hay actividad reciente para mostrar.
                            </div>

                        </div>

                    ) : (

                        visibleNotifications.map(
                            notification => {

                                const Icon =
                                    getIcon(
                                        notification.type
                                    );


                                return (

                                    <button
                                        key={
                                            notification.id
                                        }

                                        type="button"

                                        onClick={() => {

                                            if (
                                                notification.type ===
                                                "appointment"
                                            ) {

                                                onClose();

                                                onNavigate(
                                                    "citas"
                                                );

                                                return;

                                            }


                                            if (
                                                notification.type ===
                                                "whatsapp"
                                            ) {

                                                onClose();

                                                onNavigate(
                                                    "whatsapp"
                                                );

                                                return;

                                            }


                                            if (
                                                notification.type ===
                                                "payment"
                                            ) {

                                                onClose();

                                                onNavigate(
                                                    "finanzas"
                                                );

                                                return;

                                            }

                                        }}

                                        style={{
                                            position:
                                                "relative",

                                            width:
                                                "100%",

                                            border:
                                                "1px solid #e8edf5",

                                            borderRadius:
                                                16,

                                            background:
                                                "#ffffff",

                                            padding:
                                                14,

                                            marginBottom:
                                                10,

                                            display:
                                                "flex",

                                            alignItems:
                                                "flex-start",

                                            gap:
                                                12,

                                            textAlign:
                                                "left",

                                            cursor:
                                                "pointer",

                                            boxShadow:
                                                "0 2px 8px rgba(15,23,42,0.035)",

                                            WebkitTapHighlightColor:
                                                "transparent"
                                        }}
                                    >

                                        {/* ICONO */}

                                        <div
                                            style={{
                                                width:
                                                    40,

                                                height:
                                                    40,

                                                borderRadius:
                                                    12,

                                                background:
                                                    "#eff6ff",

                                                color:
                                                    "#2563eb",

                                                display:
                                                    "flex",

                                                alignItems:
                                                    "center",

                                                justifyContent:
                                                    "center",

                                                flexShrink:
                                                    0
                                            }}
                                        >

                                            <Icon
                                                size={
                                                    20
                                                }

                                                strokeWidth={
                                                    1.9
                                                }
                                            />

                                        </div>


                                        {/* TEXTO */}

                                        <div
                                            style={{
                                                flex:
                                                    1,

                                                minWidth:
                                                    0
                                            }}
                                        >

                                            <div
                                                style={{
                                                    display:
                                                        "flex",

                                                    alignItems:
                                                        "center",

                                                    gap:
                                                        7
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        fontSize:
                                                            14,

                                                        fontWeight:
                                                            notification.unread
                                                                ? 750
                                                                : 650,

                                                        color:
                                                            "#0f172a"
                                                    }}
                                                >
                                                    {
                                                        notification.title
                                                    }
                                                </div>


                                                {notification.unread && (

                                                    <div
                                                        style={{
                                                            width:
                                                                7,

                                                            height:
                                                                7,

                                                            borderRadius:
                                                                "50%",

                                                            background:
                                                                "#2563eb",

                                                            flexShrink:
                                                                0
                                                        }}
                                                    />

                                                )}

                                            </div>


                                            <div
                                                style={{
                                                    marginTop:
                                                        4,

                                                    fontSize:
                                                        12.5,

                                                    lineHeight:
                                                        1.4,

                                                    color:
                                                        "#64748b"
                                                }}
                                            >
                                                {
                                                    notification.description
                                                }
                                            </div>


                                            <div
                                                style={{
                                                    marginTop:
                                                        7,

                                                    fontSize:
                                                        11,

                                                    color:
                                                        "#94a3b8",

                                                    fontWeight:
                                                        500
                                                }}
                                            >
                                                {
                                                    notification.time
                                                }
                                            </div>

                                        </div>

                                    </button>

                                );

                            }
                        )

                    )}

                </div>

            </div>

        </>

    );

}
